/** 基础可制作性检查（近似）。
 *
 * 明确局限（规格书 8、11）：
 * - 采用栅格采样，结果为**风险提示**，不是物理保证；
 * - 分辨率影响结果，界面与文档必须说明，并在测试中用两档分辨率对比稳定性；
 * - 不证明图案一定能剪成功，最终结论以实物试剪为准。
 */
import type { CutShape, FinishedSize, FoldId, Polygon, ValidationIssue } from '../types';
import { getFold } from '../folds/definitions';
import { computeUnfold, paperGeometry } from '../folds/geometry';
import type { MultiPoly } from '../geometry/boolean';
import { area, boundsOf, meanEdgeLength } from '../geometry/polygon';

export interface AnalyzeOptions {
  /** 栅格边长（mm），默认 0.6；越小越准但越慢。 */
  cellMm?: number;
  /** 最小连接宽度阈值（mm），默认取折法建议值。 */
  minBridgeMm?: number;
  /** 小于该尺寸的剪口视为"过小"（mm）。 */
  minHoleMm?: number;
}

export interface AnalyzableMetrics {
  paperAreaMm2: number;
  removedAreaMm2: number;
  remainingAreaMm2: number;
  removedRatio: number;
  /** 连通块数量（剩余纸）。 */
  componentCount: number;
  /** 与主体断开的部分数量。 */
  isolatedCount: number;
  /** 最大孤立块面积。 */
  largestIsolatedMm2: number;
  /** 检测窄连接所用的阈值（mm）。 */
  thinBridgeThresholdMm: number;
  /** 阈值下被"侵蚀"切断的连接数（近似）。 */
  narrowBridgeCount: number;
  /** 过小剪口数量。 */
  tinyHoleCount: number;
  cellMm: number;
  gridWidth: number;
  gridHeight: number;
}

export interface AnalyzeResult {
  metrics: AnalyzableMetrics;
  issues: ValidationIssue[];
  /** 近似标记，界面必须展示。 */
  approximate: true;
}

/** 点是否在 MultiPoly 内（奇偶规则）。 */
function insideMulti(mp: MultiPoly, x: number, y: number): boolean {
  let inside = false;
  for (const rings of mp) {
    for (const ring of rings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i];
        const b = ring[j];
        if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
      }
    }
  }
  return inside;
}

interface Grid {
  cells: Uint8Array;
  w: number;
  h: number;
  removedCells: number;
  remainingCells: number;
}

function rasterize(paperB: ReturnType<typeof boundsOf>, paperPoly: Polygon, holes: MultiPoly, cell: number): Grid {
  const w = Math.max(2, Math.ceil((paperB.maxX - paperB.minX) / cell));
  const h = Math.max(2, Math.ceil((paperB.maxY - paperB.minY) / cell));
  const cells = new Uint8Array(w * h); // 1 = 纸还在
  let removedCells = 0;
  let remainingCells = 0;
  for (let j = 0; j < h; j++) {
    const y = paperB.minY + (j + 0.5) * cell;
    for (let i = 0; i < w; i++) {
      const x = paperB.minX + (i + 0.5) * cell;
      if (!insideMulti([[paperPoly]], x, y)) {
        cells[j * w + i] = 0;
        continue;
      }
      if (insideMulti(holes, x, y)) {
        cells[j * w + i] = 0;
        removedCells++;
      } else {
        cells[j * w + i] = 1;
        remainingCells++;
      }
    }
  }
  return { cells, w, h, removedCells, remainingCells };
}

function componentsOf(cells: Uint8Array, w: number, h: number): { labels: Int32Array; sizes: number[] } {
  const labels = new Int32Array(w * h).fill(-1);
  const sizes: number[] = [];
  const stack: number[] = [];
  let current = 0;
  for (let idx = 0; idx < cells.length; idx++) {
    if (!cells[idx] || labels[idx] !== -1) continue;
    let size = 0;
    stack.push(idx);
    labels[idx] = current;
    while (stack.length) {
      const p = stack.pop()!;
      size++;
      const x = p % w;
      const nbrs = [p - 1, p + 1, p - w, p + w];
      for (let k = 0; k < 4; k++) {
        if (k === 0 && x === 0) continue;
        if (k === 1 && x === w - 1) continue;
        const n = nbrs[k];
        if (n < 0 || n >= cells.length) continue;
        if (cells[n] && labels[n] === -1) {
          labels[n] = current;
          stack.push(n);
        }
      }
    }
    sizes.push(size);
    current++;
  }
  return { labels, sizes };
}

/** 圆形结构元腐蚀：半径 r 的圆内必须全是纸，该格才保留。 */
function erode(cells: Uint8Array, w: number, h: number, radius: number): Uint8Array {
  const r = Math.max(1, Math.round(radius));
  const offsets: Array<[number, number]> = [];
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy <= r * r) offsets.push([dx, dy]);
    }
  }
  const out = new Uint8Array(cells.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      if (!cells[p]) continue;
      let ok = true;
      for (const [dx, dy] of offsets) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h || !cells[ny * w + nx]) {
          ok = false;
          break;
        }
      }
      if (ok) out[p] = 1;
    }
  }
  return out;
}

export function analyze(
  foldId: FoldId,
  size: FinishedSize,
  cuts: CutShape[],
  options: AnalyzeOptions = {},
): AnalyzeResult {
  const fold = getFold(foldId);
  const cellMm = options.cellMm ?? (fold.layers >= 16 ? 0.8 : 0.6);
  const minBridge = options.minBridgeMm ?? fold.minBridgeMm;
  const minHole = options.minHoleMm ?? 1.5;

  const geo = paperGeometry(foldId, size);
  const paperArea = area(geo.paper);
  const result = computeUnfold(foldId, size, cuts);
  const holesArea = simpleMultiArea(result.holes);

  const paperB = boundsOf(geo.paper);
  const grid = rasterize(paperB, geo.paper, result.holes, cellMm);
  const { sizes } = componentsOf(grid.cells, grid.w, grid.h);
  const cellArea = cellMm * cellMm;
  const componentAreas = sizes.map((s) => s * cellArea);
  const largest = componentAreas.length ? Math.max(...componentAreas) : 0;
  const isolated = componentAreas.filter((a) => a < largest - 1e-9 && a > 0).filter((a) => a >= cellArea * 2);

  // 窄连接检测：腐蚀掉"半径 < minBridge/2"的部分，如果连通块变多，说明存在窄连接。
  const radiusCells = Math.max(1, Math.round(minBridge / 2 / cellMm));
  const eroded = erode(grid.cells, grid.w, grid.h, radiusCells);
  const erodedSizes = componentsOf(eroded, grid.w, grid.h).sizes;
  const narrowBridgeCount = Math.max(0, erodedSizes.length - sizes.length);

  let tinyHoleCount = 0;
  for (const cut of cuts) {
    if (cut.kind === 'circle' && cut.radius * 2 < minHole) tinyHoleCount++;
    if (cut.kind === 'polygon' || cut.kind === 'freehand') {
      const b = boundsOf(cut.points);
      const minSide = Math.min(b.maxX - b.minX, b.maxY - b.minY);
      if (minSide < minHole || meanEdgeLength(cut.points) < 0.8) tinyHoleCount++;
    }
  }

  const removedRatio = paperArea > 0 ? holesArea / paperArea : 0;

  const metrics: AnalyzableMetrics = {
    paperAreaMm2: paperArea,
    removedAreaMm2: holesArea,
    remainingAreaMm2: Math.max(0, paperArea - holesArea),
    removedRatio,
    componentCount: sizes.length,
    isolatedCount: isolated.length,
    largestIsolatedMm2: isolated.length ? Math.max(...isolated) : 0,
    thinBridgeThresholdMm: minBridge,
    narrowBridgeCount,
    tinyHoleCount,
    cellMm,
    gridWidth: grid.w,
    gridHeight: grid.h,
  };

  const issues: ValidationIssue[] = [];
  if (result.degraded) {
    issues.push({
      level: 'warn',
      code: 'geometry.degraded',
      message: '几何运算在复杂输入下发生降级，展开结果可能不完整，建议简化剪口。',
    });
  }
  if (grid.remainingCells === 0 || removedRatio >= 0.995) {
    issues.push({ level: 'error', code: 'all-cut', message: '整张纸都被剪掉了，请减少剪口面积。' });
  } else if (removedRatio > 0.75) {
    issues.push({
      level: 'warn',
      code: 'mostly-cut',
      message: `约 ${(removedRatio * 100).toFixed(0)}% 的纸张被剪掉，成品会很脆弱。`,
    });
  }
  if (isolated.length > 0) {
    issues.push({
      level: 'warn',
      code: 'isolated',
      message: `检测到 ${isolated.length} 处可能与主体断开的部分（最大约 ${metrics.largestIsolatedMm2.toFixed(1)} mm²），展开后会脱落。`,
    });
  }
  if (narrowBridgeCount > 0) {
    issues.push({
      level: 'warn',
      code: 'thin-bridge',
      message: `按 ${minBridge} mm 连接宽度估算，有 ${narrowBridgeCount} 处连接偏窄，${fold.layers} 层时容易断。`,
    });
  }
  if (tinyHoleCount > 0) {
    issues.push({
      level: 'warn',
      code: 'tiny-holes',
      message: `有 ${tinyHoleCount} 个剪口小于 ${minHole} mm，实际用剪刀很难剪准。`,
    });
  }
  if (fold.layers >= 16) {
    issues.push({
      level: 'info',
      code: 'thick-stack',
      message: `${fold.layers} 层叠纸较厚，建议使用 ${fold.recommendedGsm} g/m² 的薄纸，并先做简单图案。`,
    });
  }
  if (fold.difficulty >= 4) {
    issues.push({
      level: 'info',
      code: 'difficulty',
      message: `这是较难的折法（难度 ${fold.difficulty}/5），不建议作为儿童活动。`,
    });
  }

  return { metrics, issues, approximate: true };
}

function simpleMultiArea(mp: MultiPoly): number {
  let total = 0;
  for (const rings of mp) {
    rings.forEach((ring, i) => {
      let s = 0;
      for (let k = 0; k < ring.length; k++) {
        const p = ring[k];
        const q = ring[(k + 1) % ring.length];
        s += p.x * q.y - q.x * p.y;
      }
      total += (i === 0 ? 1 : -1) * Math.abs(s / 2);
    });
  }
  return total;
}